import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus, Truck, Printer, ChevronRight, ChevronLeft, Trash2, Eye, Pencil } from 'lucide-react';
import { useDeliveryNotePrint, DeliveryNoteView } from './DeliveryNotePrint';
import { formatDate } from '../../utils/dateFormatter';
import { canEditOrder } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DeliveryNotes = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const { handlePrint } = useDeliveryNotePrint();

  const [notes, setNotes] = useState([]);
  const [viewNote, setViewNote] = useState(null);
  const [clientFilter, setClientFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1);

  // Step 1 — client
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  // Step 2 — orders + quantities
  const [pendingOrders, setPendingOrders] = useState([]);
  const [orderSearch, setOrderSearch] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [deliveredQty, setDeliveredQty] = useState({}); // { [order_id]: { description: qty } }
  const [partialQty, setPartialQty] = useState({});     // { [`${orderId}_${desc}`]: qty }
  const [ruinedQty, setRuinedQty] = useState({});       // { [`${orderId}_${desc}`]: qty }

  // Step 3 — note details
  const [deliveryDate, setDeliveryDate] = useState(new Date().toISOString().split('T')[0]);
  const [noteNotes, setNoteNotes] = useState('');
  const [nextNumber, setNextNumber] = useState('');

  // Quantity-only edit of an uninvoiced note. `editQtyLines` mirrors the note's
  // stored lines; only the two quantity fields are ever sent back.
  const [editQtyNote, setEditQtyNote] = useState(null);
  const [editQtyLines, setEditQtyLines] = useState([]);
  const [isEditQtySubmitting, setIsEditQtySubmitting] = useState(false);

  const openEditQty = (note) => {
    setEditQtyLines((note.items || []).map(it => ({
      order_id: it.order_id,
      description: it.description,
      item_code: it.item_code,
      order_number: it.order_number,
      quantity: it.quantity ?? 0,
      ruined_quantity: it.ruined_quantity ?? 0,
    })));
    setEditQtyNote(note);
  };

  const updateEditQtyLine = (idx, field, value) => {
    const num = Math.max(0, parseInt(value) || 0);
    setEditQtyLines(prev => prev.map((l, i) => (i === idx ? { ...l, [field]: num } : l)));
  };

  const handleEditQtySubmit = async () => {
    if (isEditQtySubmitting) return;
    if (editQtyLines.every(l => l.quantity === 0 && l.ruined_quantity === 0)) {
      toast.error('At least one line must have a quantity');
      return;
    }
    setIsEditQtySubmitting(true);
    try {
      const res = await axios.patch(
        `${API}/delivery-notes/${editQtyNote.id}/quantities`,
        {
          items: editQtyLines.map(l => ({
            order_id: l.order_id,
            description: l.description,
            quantity: l.quantity,
            ruined_quantity: l.ruined_quantity,
          })),
        },
        { headers: getAuthHeader() },
      );
      toast.success('Quantities updated');
      const fulfilled = res.data?.fulfilled_orders || [];
      if (fulfilled.length > 0) {
        toast.success(`Order(s) now fulfilled: ${fulfilled.join(', ')}`);
      }
      setEditQtyNote(null);
      fetchNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update quantities');
    } finally {
      setIsEditQtySubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this delivery note?')) return;
    try {
      await axios.delete(`${API}/delivery-notes/${noteId}`, { headers: getAuthHeader() });
      toast.success('Delivery note deleted');
      fetchNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const fetchNotes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/delivery-notes`, { headers: getAuthHeader() });
      setNotes(res.data);
    } catch {
      toast.error('Failed to fetch delivery notes');
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const openDialog = async () => {
    setStep(1);
    setSelectedClient(null);
    setPendingOrders([]);
    setOrderSearch('');
    setSelectedOrderIds(new Set());
    setDeliveredQty({});
    setPartialQty({});
    setRuinedQty({});
    setDeliveryDate(new Date().toISOString().split('T')[0]);
    setNoteNotes('');
    try {
      const [clientsRes, numRes] = await Promise.all([
        axios.get(`${API}/clients`, { headers: getAuthHeader() }),
        axios.get(`${API}/delivery-notes/next-number`, { headers: getAuthHeader() }),
      ]);
      setClients(clientsRes.data);
      setNextNumber(numRes.data.next_number);
    } catch {
      toast.error('Failed to initialize form');
    }
    setIsOpen(true);
  };

  const handleClientSelect = async (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setSelectedClient(client || null);
    setSelectedOrderIds(new Set());
    setDeliveredQty({});
    setPartialQty({});
    if (!clientId) { setPendingOrders([]); return; }
    try {
      const res = await axios.get(`${API}/orders?client_id=${clientId}`, { headers: getAuthHeader() });
      setPendingOrders(res.data.filter(o => o.status === 'pending' || o.status === 'in_production'));
    } catch {
      toast.error('Failed to fetch orders');
    }
  };

  const handleOrderToggle = async (orderId, checked) => {
    const next = new Set(selectedOrderIds);
    if (checked) {
      next.add(orderId);
      if (!deliveredQty[orderId]) {
        try {
          const res = await axios.get(`${API}/orders/${orderId}/delivered`, { headers: getAuthHeader() });
          setDeliveredQty(prev => ({ ...prev, [orderId]: res.data }));
        } catch {
          setDeliveredQty(prev => ({ ...prev, [orderId]: {} }));
        }
      }
    } else {
      next.delete(orderId);
    }
    setSelectedOrderIds(next);
  };

  const qtyKey = (orderId, desc) => `${orderId}_${desc}`;

  const getRemaining = (order, variant) => {
    const desc = `${order.items} - ${variant.size} / ${variant.color}`;
    const alreadyDelivered = (deliveredQty[order.id] || {})[desc] || 0;
    return variant.quantity - alreadyDelivered;
  };

  const handleQtyChange = (orderId, desc, value, remaining) => {
    const num = Math.min(remaining, Math.max(0, parseInt(value) || 0));
    setPartialQty(prev => ({ ...prev, [qtyKey(orderId, desc)]: num }));
  };

  const handleRuinedQtyChange = (orderId, desc, value) => {
    const num = Math.max(0, parseInt(value) || 0);
    setRuinedQty(prev => ({ ...prev, [qtyKey(orderId, desc)]: num }));
  };

  const handleSubmit = async () => {
    for (const order of pendingOrders.filter(o => selectedOrderIds.has(o.id))) {
      for (const v of order.variants) {
        const desc = `${order.items} - ${v.size} / ${v.color}`;
        const delivered = (deliveredQty[order.id] || {})[desc] || 0;
        const remaining = v.quantity - delivered;
        const qty = partialQty[qtyKey(order.id, desc)] || 0;
        const rQty = ruinedQty[qtyKey(order.id, desc)] || 0;
        if (qty + rQty > remaining) {
          toast.error(`"${desc}": delivery + ruined (${qty + rQty}) exceeds remaining (${remaining})`);
          return;
        }
      }
    }
    const allItems = [];
    for (const order of pendingOrders.filter(o => selectedOrderIds.has(o.id))) {
      for (const v of order.variants) {
        const desc = `${order.items} - ${v.size} / ${v.color}`;
        const qty = partialQty[qtyKey(order.id, desc)] || 0;
        const rQty = ruinedQty[qtyKey(order.id, desc)] || 0;
        if (qty > 0 || rQty > 0) {
          allItems.push({
            description: desc,
            quantity: qty,
            ruined_quantity: rQty,
            unit_price: order.item_price || 0,
            order_id: order.id,
            order_number: order.order_number,
            item_code: order.item_code,
            card_number: order.card_number || null,
            procurement_date: order.procurement_date || null,
            production_date: order.production_date || null,
          });
        }
      }
    }

    if (allItems.length === 0) {
      toast.error('Enter at least one quantity to deliver or write off');
      return;
    }

    const noteData = {
      client_id: selectedClient.id,
      client_name: selectedClient.name,
      items: allItems,
      delivery_date: deliveryDate,
      notes: noteNotes || null,
      order_ids: [...new Set(allItems.map(i => i.order_id))],
    };

    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/delivery-notes`, noteData, { headers: getAuthHeader() });
      toast.success(`Delivery note ${res.data.number} created`);
      if (res.data.fulfilled_orders?.length > 0) {
        res.data.fulfilled_orders.forEach(n => toast.success(`Order #${n} fully fulfilled!`));
      }
      setIsOpen(false);
      fetchNotes();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      toast.error(`Failed to create delivery note: ${detail}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Validation per step
  const canProceedStep1 = selectedClient !== null;
  const canProceedStep2 = selectedOrderIds.size > 0 && (() => {
    for (const order of pendingOrders.filter(o => selectedOrderIds.has(o.id))) {
      for (const v of order.variants) {
        const desc = `${order.items} - ${v.size} / ${v.color}`;
        if ((partialQty[qtyKey(order.id, desc)] || 0) > 0) return true;
        if ((ruinedQty[qtyKey(order.id, desc)] || 0) > 0) return true;
      }
    }
    return false;
  })();

  return (
    <Card className="p-6 border border-border">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('deliveryNotes')}</h2>
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open) setIsOpen(false); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-accent hover:bg-accent/90" onClick={openDialog} data-testid="add-delivery-note-btn">
              <Plus className="w-4 h-4" />
              <span lang={language === 'ar' ? 'ar' : 'en'}>{t('add')}</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>
                {language === 'ar' ? 'إنشاء إشعار تسليم' : 'Create Delivery Note'}
                {nextNumber && <span className="text-sm font-normal text-muted-foreground ml-2">— {nextNumber}</span>}
              </DialogTitle>
            </DialogHeader>

            {/* Step indicator */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              {(language === 'ar' ? ['العميل', 'الطلبات والكميات', 'التفاصيل'] : ['Client', 'Orders & Quantities', 'Details']).map((label, i) => (
                <React.Fragment key={i}>
                  <span className={step === i + 1 ? 'font-semibold text-foreground' : ''}>{i + 1}. {label}</span>
                  {i < 2 && <ChevronRight className="w-3 h-3" />}
                </React.Fragment>
              ))}
            </div>

            {/* Step 1: Select client */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label>{language === 'ar' ? 'العميل' : 'Client'}</Label>
                  <select
                    value={selectedClient?.id || ''}
                    onChange={(e) => handleClientSelect(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">{language === 'ar' ? 'اختر عميل' : 'Select client'}</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                {selectedClient && pendingOrders.length === 0 && (
                  <p className="text-sm text-muted-foreground">{language === 'ar' ? 'لا توجد طلبات معلقة لهذا العميل' : 'No pending orders for this client.'}</p>
                )}
                <div className="flex justify-end">
                  <Button onClick={() => setStep(2)} disabled={!canProceedStep1 || pendingOrders.length === 0} className="gap-2 bg-accent hover:bg-accent/90">
                    {language === 'ar' ? 'التالي' : 'Next'} <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Select orders + quantities */}
            {step === 2 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">{language === 'ar' ? 'اختر الطلبات وأدخل الكميات المراد تسليمها' : 'Select orders and enter quantities to deliver now.'}</p>
                <Input
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  placeholder={language === 'ar' ? 'بحث بالكارت أو كود الصنف...' : 'Search by card or item code...'}
                />
                <div className="space-y-4">
                  {pendingOrders.filter(o => {
                    if (!orderSearch.trim()) return true;
                    const q = orderSearch.toLowerCase();
                    return (o.card_number || '').toLowerCase().includes(q) || (o.item_code || '').toLowerCase().includes(q);
                  }).map(order => (
                    <div key={order.id} className="border border-border rounded-md p-3">
                      <label className="flex items-center gap-2 font-medium cursor-pointer mb-3">
                        <input
                          type="checkbox"
                          checked={selectedOrderIds.has(order.id)}
                          onChange={(e) => handleOrderToggle(order.id, e.target.checked)}
                          className="w-4 h-4"
                        />
                        Order #{order.order_number}{order.card_number ? ` / Card: ${order.card_number}` : ''} — {order.items}
                        {order.item_code && <span className="text-xs text-muted-foreground">({order.item_code})</span>}
                      </label>
                      {selectedOrderIds.has(order.id) && (
                        <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                          <thead>
                            <tr className="bg-muted/50">
                              <th className="border border-border p-2 text-start">{language === 'ar' ? 'المقاس / اللون' : 'Variant'}</th>
                              <th className="border border-border p-2 text-center">{language === 'ar' ? 'الإجمالي' : 'Total'}</th>
                              <th className="border border-border p-2 text-center">{language === 'ar' ? 'المُسلَّم' : 'Delivered'}</th>
                              <th className="border border-border p-2 text-center">{language === 'ar' ? 'المتبقي' : 'Remaining'}</th>
                              <th className="border border-border p-2 text-center">{language === 'ar' ? 'يُسلَّم الآن' : 'Deliver Now'}</th>
                              <th className="border border-border p-2 text-center text-destructive">{language === 'ar' ? 'تالف' : 'Ruined'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.variants.map((v, vi) => {
                              const desc = `${order.items} - ${v.size} / ${v.color}`;
                              const delivered = (deliveredQty[order.id] || {})[desc] || 0;
                              const remaining = v.quantity - delivered;
                              const deliverNow = partialQty[qtyKey(order.id, desc)] || 0;
                              const maxRuined = remaining - deliverNow;
                              return (
                                <tr key={vi}>
                                  <td className="border border-border p-2">{v.size} / {v.color}</td>
                                  <td className="border border-border p-2 text-center font-mono-numeric">{v.quantity}</td>
                                  <td className="border border-border p-2 text-center font-mono-numeric text-muted-foreground">{delivered}</td>
                                  <td className="border border-border p-2 text-center font-mono-numeric">{remaining}</td>
                                  <td className="border border-border p-2 text-center">
                                    {remaining > 0 ? (
                                      <input
                                        type="number"
                                        min={0}
                                        max={remaining}
                                        value={partialQty[qtyKey(order.id, desc)] ?? ''}
                                        onChange={(e) => handleQtyChange(order.id, desc, e.target.value, remaining)}
                                        className="w-20 h-8 rounded border border-input bg-background px-2 text-sm text-center"
                                        placeholder="0"
                                      />
                                    ) : (
                                      <span className="text-xs text-muted-foreground">Done</span>
                                    )}
                                  </td>
                                  <td className="border border-border p-2 text-center">
                                    {maxRuined > 0 ? (
                                      <input
                                        type="number"
                                        min={0}
                                        max={maxRuined}
                                        value={ruinedQty[qtyKey(order.id, desc)] ?? ''}
                                        onChange={(e) => handleRuinedQtyChange(order.id, desc, e.target.value)}
                                        className="w-20 h-8 rounded border border-destructive/50 bg-background px-2 text-sm text-center"
                                        placeholder="0"
                                      />
                                    ) : (
                                      <span className="text-xs text-muted-foreground">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)} className="gap-2">
                    <ChevronLeft className="w-4 h-4" /> {language === 'ar' ? 'رجوع' : 'Back'}
                  </Button>
                  <Button onClick={() => setStep(3)} disabled={!canProceedStep2} className="gap-2 bg-accent hover:bg-accent/90">
                    {language === 'ar' ? 'التالي' : 'Next'} <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Details */}
            {step === 3 && (
              <div className="space-y-4">
                <div>
                  <Label>{language === 'ar' ? 'تاريخ التسليم' : 'Delivery Date'}</Label>
                  <Input
                    type="date"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    required
                    data-testid="note-delivery-date"
                  />
                </div>
                <div>
                  <Label>{language === 'ar' ? 'ملاحظات (اختياري)' : 'Notes (optional)'}</Label>
                  <Input
                    value={noteNotes}
                    onChange={(e) => setNoteNotes(e.target.value)}
                    placeholder={language === 'ar' ? 'ملاحظات إضافية' : 'Additional delivery notes'}
                    data-testid="note-notes-input"
                  />
                </div>
                <div className="bg-muted/30 p-3 rounded-md text-sm space-y-1">
                  <p className="font-medium">{language === 'ar' ? 'ملخص' : 'Summary'}</p>
                  <p>{language === 'ar' ? 'العميل:' : 'Client:'} {selectedClient?.name}</p>
                  <p>{language === 'ar' ? 'الطلبات:' : 'Orders:'} {[...selectedOrderIds].map(id => {
                    const o = pendingOrders.find(x => x.id === id);
                    return o ? `#${o.order_number}` : id;
                  }).join(', ')}</p>
                  <p>{language === 'ar' ? 'إجمالي القطع للتسليم:' : 'Total items to deliver:'} {(() => {
                    let total = 0;
                    for (const order of pendingOrders.filter(o => selectedOrderIds.has(o.id))) {
                      for (const v of order.variants) {
                        const desc = `${order.items} - ${v.size} / ${v.color}`;
                        total += partialQty[qtyKey(order.id, desc)] || 0;
                      }
                    }
                    return total;
                  })()}</p>
                </div>
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(2)} className="gap-2">
                    <ChevronLeft className="w-4 h-4" /> {language === 'ar' ? 'رجوع' : 'Back'}
                  </Button>
                  <Button onClick={handleSubmit} className="bg-accent hover:bg-accent/90" data-testid="note-submit-btn" disabled={isSubmitting}>
                    <span lang={language === 'ar' ? 'ar' : 'en'}>{language === 'ar' ? 'إنشاء إشعار تسليم' : 'Create Delivery Note'}</span>
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      {(() => {
        const uniqueClients = [...new Set(notes.map(n => n.client_name).filter(Boolean))].sort();
        const filteredNotes = notes.filter(note => {
          const matchesClient = !clientFilter || note.client_name === clientFilter;
          const matchesFrom = !dateFrom || note.delivery_date >= dateFrom;
          const matchesTo = !dateTo || note.delivery_date <= dateTo;
          return matchesClient && matchesFrom && matchesTo;
        });
        return (
          <>
            <div className="flex flex-wrap gap-3 mb-4 items-end">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Client</label>
                <select className="border border-border rounded px-2 py-1.5 text-sm bg-background"
                  value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
                  <option value="">All Clients</option>
                  {uniqueClients.map((c, i) => <option key={i} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">From</label>
                <input type="date" className="border border-border rounded px-2 py-1.5 text-sm bg-background"
                  value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">To</label>
                <input type="date" className="border border-border rounded px-2 py-1.5 text-sm bg-background"
                  value={dateTo} onChange={e => setDateTo(e.target.value)} />
              </div>
              {(clientFilter || dateFrom || dateTo) && (
                <Button variant="ghost" size="sm" onClick={() => { setClientFilter(''); setDateFrom(''); setDateTo(''); }}>
                  Clear
                </Button>
              )}
            </div>

            {/* Delivery notes list */}
            <div className="space-y-4">
              {filteredNotes.map((note, idx) => {
          const orderNums = note.order_ids?.length > 0
            ? [...new Set(note.items?.map(i => i.order_number).filter(Boolean))].map(n => `#${n}`).join(', ')
            : note.order_id ? `#${note.order_id}` : '—';
          return (
            <Card key={idx} className="p-4 border border-border hover:border-slate-300 transition-colors" data-testid={`delivery-note-card-${idx}`}>
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Truck className="w-5 h-5" />
                      {note.note_number}
                      {note.invoiced && <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">Invoiced</span>}
                    </h3>
                    <p className="text-sm text-muted-foreground">Orders: {orderNums}</p>
                    <p className="text-sm font-medium mt-1">{note.client_name}</p>
                  </div>
                  <div className="text-end">
                    <p className="text-sm text-muted-foreground">Delivery Date</p>
                    <p className="text-sm font-semibold">{formatDate(note.delivery_date)}</p>
                  </div>
                </div>
                {note.items && note.items.length > 0 && (
                  <div className="border-t border-border pt-3">
                    <p className="text-sm font-medium mb-2">Items:</p>
                    <ul className="text-sm space-y-1 text-muted-foreground">
                      {note.items.map((item, iidx) => (
                        <li key={iidx}>
                          • {item.item_code && <span className="font-mono-numeric font-semibold text-foreground mr-1">[{item.item_code}]</span>}{item.description} (×{item.quantity})
                          {item.ruined_quantity > 0 && (
                            <span className="ml-2 text-xs text-destructive font-medium">+{item.ruined_quantity} incomplete</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {note.notes && (
                  <p className="text-sm text-muted-foreground border-t border-border pt-2">
                    <span className="font-medium">Notes:</span> {note.notes}
                  </p>
                )}
                <div className="pt-2 border-t border-border mt-2 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setViewNote(note)} className="gap-2">
                    <Eye className="w-4 h-4" />
                    View
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handlePrint(note)} className="gap-2">
                    <Printer className="w-4 h-4" />
                    Print
                  </Button>
                  {canEditOrder(user?.role) && !note.invoiced && (
                    <Button size="sm" variant="outline" onClick={() => openEditQty(note)} className="gap-2" data-testid={`edit-dn-qty-${idx}`}>
                      <Pencil className="w-4 h-4" />
                      Edit Qty
                    </Button>
                  )}
                  {user?.role === 'admin' && !note.invoiced && (
                    <Button size="sm" variant="outline" onClick={() => handleDeleteNote(note.id)} className="gap-2 text-destructive hover:text-destructive">
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </Card>
              );
            })}
            </div>
            {filteredNotes.length === 0 && (
              <div className="text-center py-12 text-muted-foreground" data-testid="empty-delivery-notes">
                <p lang={language === 'ar' ? 'ar' : 'en'}>{notes.length === 0 ? t('noDeliveryNotesYet') : t('noDeliveryNotesMatch')}</p>
              </div>
            )}
          </>
        );
      })()}
    <Dialog open={!!viewNote} onOpenChange={open => { if (!open) setViewNote(null); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {viewNote && <DeliveryNoteView note={viewNote} />}
      </DialogContent>
    </Dialog>

    <Dialog open={!!editQtyNote} onOpenChange={open => { if (!open) setEditQtyNote(null); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Quantities — {editQtyNote?.note_number}</DialogTitle>
        </DialogHeader>
        {editQtyNote && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {editQtyNote.client_name} · {formatDate(editQtyNote.delivery_date)}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="border border-border p-2 text-start font-semibold">Item</th>
                    <th className="border border-border p-2 text-end font-semibold w-28">Delivered</th>
                    <th className="border border-border p-2 text-end font-semibold w-28">Incomplete</th>
                  </tr>
                </thead>
                <tbody>
                  {editQtyLines.map((line, idx) => (
                    <tr key={`${line.order_id}-${line.description}`} className="hover:bg-muted/30">
                      <td className="border border-border p-2">
                        {line.item_code && (
                          <span className="font-mono-numeric font-semibold mr-1">[{line.item_code}]</span>
                        )}
                        {line.description}
                        <span className="block text-xs text-muted-foreground font-mono-numeric" dir="ltr">{line.order_number}</span>
                      </td>
                      <td className="border border-border p-1">
                        <Input
                          type="number"
                          min="0"
                          dir="ltr"
                          className="text-end font-mono-numeric h-9"
                          value={line.quantity}
                          onChange={e => updateEditQtyLine(idx, 'quantity', e.target.value)}
                        />
                      </td>
                      <td className="border border-border p-1">
                        <Input
                          type="number"
                          min="0"
                          dir="ltr"
                          className="text-end font-mono-numeric h-9"
                          value={line.ruined_quantity}
                          onChange={e => updateEditQtyLine(idx, 'ruined_quantity', e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">
              Only quantities can be changed. Lowering a quantity may return its order to In Production.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditQtyNote(null)}>Cancel</Button>
              <Button onClick={handleEditQtySubmit} disabled={isEditQtySubmitting}>
                {isEditQtySubmitting ? 'Saving…' : 'Save Quantities'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </Card>
  );
};
